// Deployed-revision client helpers (pure/near-pure; no React).
//
// Fetches the app's own version (/api/version) and the notifier's version
// (/api/notifier-version, a server-side authenticated proxy). Both endpoints
// are debug-panel-only in the UI; this module holds the data logic so it is
// unit-testable without a React runtime.

/**
 * Short-form git SHA for display. The "dev" sentinel (local builds with no SHA
 * baked in) and any missing value pass through as "dev".
 * @param {string|null|undefined} sha
 * @returns {string}
 */
export function shortSha(sha) {
  if (!sha || sha === "dev") return "dev";
  return sha.slice(0, 7);
}

/**
 * Primary one-line label for a service's version: "<short-sha> · <revision>".
 * builtAt is rendered separately (see formatBuiltAt) to keep this line short so
 * it never forces the account panel to scroll horizontally. A null/missing info
 * (service unreachable or its /api/version failed) renders as "unknown".
 * @param {{gitSha?: string, revision?: string}|null|undefined} info
 * @returns {string}
 */
export function formatServiceVersion(info) {
  if (!info) return "unknown";
  return `${shortSha(info.gitSha)} · ${info.revision}`;
}

/**
 * Compact the ISO-8601 builtAt to minute precision for display, e.g.
 * "2026-07-13T14:59:36Z" → "2026-07-13 14:59 UTC". Empty string when absent.
 * @param {string|null|undefined} builtAt
 * @returns {string}
 */
export function formatBuiltAt(builtAt) {
  if (!builtAt) return "";
  // "YYYY-MM-DDTHH:MM:SSZ" → "YYYY-MM-DD HH:MM UTC" (pure string slice; no Date).
  return `${builtAt.slice(0, 16).replace("T", " ")} UTC`;
}

/**
 * Fetch both services' deployed-revision info in parallel.
 *
 * Degrades gracefully — never rejects: a failed /api/version yields `app: null`;
 * the notifier proxy already returns a `{ reachable, ... }` shape, and a failed
 * request to it collapses to `{ reachable: false, reason: "fetch-failed" }`.
 *
 * @param {{ fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<{ app: object|null, notifier: object }>}
 */
export async function fetchDeployedRevision({ fetchImpl = fetch } = {}) {
  // Treat a non-2xx as a failure (like lib/forecastStream.js's resp.ok check) so
  // a JSON error body never renders as a misleading "dev · undefined" line.
  const [app, notifier] = await Promise.all([
    fetchImpl("/api/version")
      .then((r) => {
        if (r.ok) return r.json();
        console.warn("version.fetch_failed", r.status);
        return null;
      })
      .catch((err) => {
        console.warn("version.fetch_failed", err);
        return null;
      }),
    fetchImpl("/api/notifier-version")
      .then((r) => (r.ok ? r.json() : { reachable: false, reason: "fetch-failed" }))
      .catch(() => ({ reachable: false, reason: "fetch-failed" })),
  ]);
  return { app, notifier };
}
